import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, CheckCircle } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const InvestorNetwork = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    investmentCapacity: '',
    sectors: [],
    investorType: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleInputChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSectorChange = (e) => {
    const value = e.target.value;
    const sectors = formData.sectors.includes(value)
      ? formData.sectors.filter(s => s !== value)
      : [...formData.sectors, value];
    
    setFormData({
      ...formData,
      sectors
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      const response = await axios.post(`${API}/investor-registration`, {
        ...formData,
        sectors: formData.sectors.join(', ')
      });
      
      if (response.data.success) {
        toast.success('Registration Successful!', {
          description: 'Our team will contact you with curated opportunities.',
        });
        
        // Reset form
        setFormData({
          name: '',
          email: '',
          phone: '',
          investmentCapacity: '',
          sectors: [],
          investorType: '',
          message: ''
        });
      } else {
        toast.error('Error', {
          description: response.data.message,
        });
      }
    } catch (error) {
      console.error('Error submitting form:', error);
      toast.error('Error', {
        description: 'Failed to submit registration. Please try again or contact us directly.',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const sectorOptions = [
    'Real Estate',
    'Infrastructure',
    'Solar & Renewable Energy',
    'Data Centres',
    'Hospitality',
    'Industrial & Logistics'
  ];

  return (
    <div className="service-detail-page">
      {/* Breadcrumb */}
      <div className="breadcrumb-container">
        <Link to="/" className="breadcrumb-link">
          <ArrowLeft size={18} />
          Back to Home
        </Link>
        <span className="breadcrumb-separator">/</span>
        <span className="breadcrumb-current">Investor Network</span>
      </div>

      {/* Hero Section */}
      <section className="service-detail-hero">
        <h1 className="service-detail-title">Investor Network</h1>
        <p className="service-detail-subtitle">
          Register to receive curated opportunities, joint venture proposals and strategic investment mandates
        </p>
      </section>

      {/* Benefits Section */}
      <section className="service-detail-section alt-bg">
        <div className="service-detail-content">
          <h2 className="service-section-title">Network Benefits</h2>
          <div className="investor-benefits-grid">
            <div className="benefit-card">
              <CheckCircle size={24} className="benefit-icon" />
              <h3 className="benefit-title">Curated Opportunities</h3>
              <p className="benefit-text">
                Access to pre-vetted investment opportunities matching your criteria and capacity
              </p>
            </div>
            <div className="benefit-card">
              <CheckCircle size={24} className="benefit-icon" />
              <h3 className="benefit-title">Joint Venture Proposals</h3>
              <p className="benefit-text">
                Strategic partnership opportunities with developers and institutional players
              </p>
            </div>
            <div className="benefit-card">
              <CheckCircle size={24} className="benefit-icon" />
              <h3 className="benefit-title">Investment Mandates</h3>
              <p className="benefit-text">
                Priority access to large-scale real estate and infrastructure mandates
              </p>
            </div>
            <div className="benefit-card">
              <CheckCircle size={24} className="benefit-icon" />
              <h3 className="benefit-title">Market Intelligence</h3>
              <p className="benefit-text">
                Insights and intelligence on market trends and emerging opportunities
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Registration Form */}
      <section className="service-detail-section">
        <div className="service-detail-content">
          <div className="investor-form-container">
            <h2 className="investor-form-title">Register Your Interest</h2>
            <p className="investor-form-subtitle">
              Complete the form below to join our exclusive investor network
            </p>

            <form className="investor-registration-form" onSubmit={handleSubmit}>
              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="name" className="form-label">Full Name *</label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    className="form-input"
                    required
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="email" className="form-label">Email Address *</label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    className="form-input"
                    required
                  />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="phone" className="form-label">Phone Number *</label>
                  <input
                    type="tel"
                    id="phone"
                    name="phone"
                    value={formData.phone}
                    onChange={handleInputChange}
                    className="form-input"
                    required
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="investorType" className="form-label">Investor Type *</label>
                  <select
                    id="investorType"
                    name="investorType"
                    value={formData.investorType}
                    onChange={handleInputChange}
                    className="form-select"
                    required
                  >
                    <option value="">Select type</option>
                    <option value="uhni">Ultra HNI / Family Office</option>
                    <option value="developer">Developer</option>
                    <option value="institutional">Institutional Investor</option>
                    <option value="corporate">Corporate / Industrialist</option>
                    <option value="other">Other</option>
                  </select>
                </div>
              </div>

              <div className="form-group">
                <label htmlFor="investmentCapacity" className="form-label">Investment Capacity *</label>
                <select
                  id="investmentCapacity"
                  name="investmentCapacity"
                  value={formData.investmentCapacity}
                  onChange={handleInputChange}
                  className="form-select"
                  required
                >
                  <option value="">Select range</option>
                  <option value="6-12m">USD 6 Million - USD 12 Million</option>
                  <option value="12-30m">USD 12 Million - USD 30 Million</option>
                  <option value="30-60m">USD 30 Million - USD 60 Million</option>
                  <option value="60m+">USD 60 Million+</option>
                  <option value="100m+">USD 100 Million+</option>
                </select>
              </div>

              <div className="form-group">
                <label className="form-label">Preferred Sectors * (Select all that apply)</label>
                <div className="checkbox-grid">
                  {sectorOptions.map((sector) => (
                    <label key={sector} className="checkbox-label">
                      <input
                        type="checkbox"
                        value={sector}
                        checked={formData.sectors.includes(sector)}
                        onChange={handleSectorChange}
                        className="checkbox-input"
                      />
                      <span className="checkbox-text">{sector}</span>
                    </label>
                  ))}
                </div>
              </div>

              <div className="form-group">
                <label htmlFor="message" className="form-label">Additional Information</label>
                <textarea
                  id="message"
                  name="message"
                  value={formData.message}
                  onChange={handleInputChange}
                  className="form-textarea"
                  rows="4"
                  placeholder="Any specific requirements or investment preferences..."
                ></textarea>
              </div>

              <button type="submit" className="btn-primary btn-large form-submit" disabled={isSubmitting}>
                {isSubmitting ? 'Submitting...' : 'Register for Investor Network'}
              </button>

              <p className="form-disclaimer">
                By registering, you agree to receive investment opportunities and updates from HSNL Advisory. 
                All information is kept strictly confidential.
              </p>
            </form>
          </div>
        </div>
      </section>
    </div>
  );
};
